import { z } from "zod";
import { fallbackTransform } from "./fallback";
import { getFieldTransform } from "./get";
import { stringJoinTransform } from "./string-join";
import { substringTransform } from "./substring";

export { fallbackTransform } from "./fallback";
export { getFieldTransform } from "./get";
export { stringJoinTransform } from "./string-join";
export { substringTransform } from "./substring";

/**
 * Expression transforms — the function library for socket expressions.
 *
 * A transform is a pure, synchronous function invoked *inside* an expression
 * (`(concatenate (output "s" "a") (output "s" "b"))`). It is NOT a graph node:
 * no StepConfig, no category, no palette entry, no StepRun. It is the typed,
 * tested counterpart to the grammar's structural primitives (`output`,
 * `concat`) — the difference is that transforms are added to a registry rather
 * than hardcoded into the parser.
 *
 * ⚠️ Every transform MUST be synchronous and pure. Expressions are evaluated
 * inside the Temporal workflow VM, which re-executes them on every replay —
 * every deploy replays every open run. So: no I/O, no `Date.now()`/
 * `new Date()`/`Math.random()`, no `await`, same input ⇒ same output forever.
 * A transform that needs any of that is a step, not a transform.
 *
 * Positional, single-valued: expression calls pass arguments left-to-right and
 * yield one value on the consuming socket — so a transform takes a tuple of
 * args and returns a single value (unlike a node with named output sockets).
 *
 * The batch-mapping and validation machinery is ported from the lambda-nodes
 * work (PR #5416); the node framing is deliberately dropped.
 */
export interface TransformDefinition<
  TArgs extends z.ZodTuple = z.ZodTuple,
  TOut extends z.ZodType = z.ZodType,
> {
  /** The function name used in expressions, e.g. `"concatenate"`. */
  readonly name: string;
  /**
   * Short label for the transform's node on the editor canvas — the expression
   * is drawn as a tiny node between its inputs and the socket it feeds, and
   * `name` is usually too long for that pill. The full `name` is what hover
   * shows, so this can be aggressively short. Defaults to `name`.
   */
  readonly label?: string;
  /**
   * One sentence on what the transform does, shown when hovering its node on
   * the canvas. Says what it DOES with its arguments — the reader can already
   * see what they are.
   */
  readonly description?: string;
  /** Positional argument schemas; the call binds args left-to-right. */
  readonly args: TArgs;
  /** Whether the transform accepts additional arguments after its minimum. */
  readonly variadic?: boolean;
  /** The single value the transform yields. */
  readonly output: TOut;
  readonly evaluate: (args: z.infer<TArgs>) => z.infer<TOut>;
}

/** Helper that preserves the arg/output types while erasing them for the registry. */
export function defineTransform<
  TArgs extends z.ZodTuple,
  TOut extends z.ZodType,
>(definition: TransformDefinition<TArgs, TOut>): TransformDefinition {
  return definition as unknown as TransformDefinition;
}

/** Which phase rejected: the transform's args or its output. */
export type TransformValidationPhase = "arguments" | "output";

export class TransformValidationError extends Error {
  constructor(
    // Not `name`: that is Error.name, overwritten below with the error type.
    readonly transformName: string,
    readonly phase: TransformValidationPhase,
    readonly detail: string,
    /** Index of the failing batch item, when the transform was mapped over one. */
    readonly itemIndex?: number,
  ) {
    super(
      `Transform "${transformName}" received invalid ${phase}${
        itemIndex === undefined ? "" : ` at batch item ${itemIndex}`
      }: ${detail}`,
    );
    this.name = "TransformValidationError";
  }
}

/** Brand key marking one failed item inside a mapped-batch transform result. */
export const TRANSFORM_ITEM_ERROR = "__transform_item_error__" as const;

/**
 * One failed item's slot in a mapped-batch transform result.
 *
 * When a transform maps over a batch, a bad item does NOT throw — its result
 * slot carries this marker instead, so the result array keeps the batch's
 * cardinality and downstream lockstep fanout still zips index-for-index with
 * the producer's other parallel arrays. Dropping the item here would misalign
 * every zip that includes another array.
 *
 * A plain branded object rather than an Error subclass because it travels as
 * DATA: through `completedSteps`, into persisted StepRun payloads, and across
 * the activity boundary as a fanned item's input — all JSON round-trips that
 * would strip a prototype.
 *
 * The marker is consumed at the fanned item's input validation
 * (`parseStepStartValue` recognizes it), which marks THAT item failed under the
 * consuming step's `onItemError` policy and reports it on the item's StepRun —
 * never silently dropped.
 */
const TransformItemErrorSchema = z.object({
  [TRANSFORM_ITEM_ERROR]: z.literal(true),
  transformName: z.string(),
  /** Index of the failing item within the producer's batch. */
  itemIndex: z.number().int().nonnegative(),
  message: z.string(),
});

export type TransformItemError = z.infer<typeof TransformItemErrorSchema>;

export function isTransformItemError(
  value: unknown,
): value is TransformItemError {
  return (
    typeof value === "object" &&
    value !== null &&
    // Cheap brand short-circuit first: this guard runs per element when
    // scanning batch-sized arrays, and almost every value lacks the key.
    (value as Record<string, unknown>)[TRANSFORM_ITEM_ERROR] === true &&
    // Then the FULL contract. The brand key alone is not proof of provenance —
    // user data on a json/unknown socket could happen to carry it, and only
    // `transformItemError` below ever constructs the complete shape. Anything
    // short of it is treated as ordinary data, never as a failed item.
    TransformItemErrorSchema.safeParse(value).success
  );
}

/**
 * The first failed-item marker carried by a resolved socket value — the value
 * itself (a fanned single-value socket) or one level inside an array (an
 * array socket receiving a mapped result whole). `undefined` when clean.
 */
export function findTransformItemError(
  value: unknown,
): TransformItemError | undefined {
  if (isTransformItemError(value)) return value;
  if (Array.isArray(value)) return value.find(isTransformItemError);
  return undefined;
}

function transformItemError(
  transformName: string,
  itemIndex: number,
  message: string,
): TransformItemError {
  return { [TRANSFORM_ITEM_ERROR]: true, transformName, itemIndex, message };
}

/**
 * The marker for a validation failure — same wording a thrown
 * TransformValidationError would carry, so the message reads identically
 * wherever the item's failure surfaces.
 */
function validationItemError(
  transformName: string,
  phase: TransformValidationPhase,
  detail: string,
  itemIndex: number,
): TransformItemError {
  return transformItemError(
    transformName,
    itemIndex,
    new TransformValidationError(transformName, phase, detail, itemIndex)
      .message,
  );
}

export const TRANSFORM_DEFINITIONS: readonly TransformDefinition[] = [
  stringJoinTransform,
  fallbackTransform,
  defineTransform(substringTransform),
  getFieldTransform,
];

const TRANSFORMS_BY_NAME = new Map<string, TransformDefinition>(
  TRANSFORM_DEFINITIONS.map((transform) => [transform.name, transform]),
);

/** Whether a call name resolves to a registered transform. */
export function isTransform(name: string): boolean {
  return TRANSFORMS_BY_NAME.has(name);
}

export function getTransform(name: string): TransformDefinition {
  const transform = TRANSFORMS_BY_NAME.get(name);
  if (!transform) {
    throw new Error(`Transform "${name}" is not registered`);
  }
  return transform;
}

/** The minimum number of positional arguments a registered transform expects. */
export function getTransformArity(name: string): number {
  return getTransform(name).args.def.items.length;
}

/** The short canvas label for a registered transform, falling back to its name. */
export function getTransformLabel(name: string): string {
  return getTransform(name).label ?? name;
}

/** One sentence on what a registered transform does, for hover help. */
export function getTransformDescription(name: string): string | undefined {
  return getTransform(name).description;
}

/**
 * How many arguments a registered transform takes, in words — the arity alone
 * is a number without a claim, and variadic-vs-fixed is the part a reader
 * actually needs before editing the expression.
 */
export function describeTransformArity(name: string): string {
  const arity = getTransformArity(name);
  const plural = arity === 1 ? "argument" : "arguments";
  return isTransformVariadic(name)
    ? `Takes ${arity} or more ${plural}.`
    : `Takes exactly ${arity} ${plural}.`;
}

/** Whether a registered transform accepts more than its minimum arguments. */
export function isTransformVariadic(name: string): boolean {
  return getTransform(name).variadic === true;
}

/**
 * The socket-type string of a transform's output, for static type-compatibility
 * checks — `"string"` / `"number"` / `"boolean"` for scalar outputs, `undefined`
 * for anything richer (so validation stays permissive rather than falsely
 * rejecting). Runtime validation in {@link evaluateTransform} is the backstop.
 */
export function getTransformOutputType(name: string): string | undefined {
  const type = getTransform(name).output.def.type;
  return type === "string" || type === "number" || type === "boolean"
    ? type
    : undefined;
}

/**
 * Validate → evaluate → validate, mapping over a batch when one is wired in.
 * Pure and synchronous, so it is safe inside the Temporal workflow sandbox.
 *
 * **Batches.** Some producers emit a batch as parallel arrays on sockets
 * declared as single values, and the engine only splits those arrays at the
 * consuming step's fanout — so an expression evaluated before that split
 * receives the arrays whole. Rather than reject them, a transform **maps over
 * the batch**: arrays are zipped item for item, single values are broadcast to
 * every item, and the result comes back as an array of the same length, still
 * aligned with the producer's arrays so downstream fanout lines up.
 *
 * The decision is by fit, not declaration: args that satisfy the tuple whole
 * are evaluated whole; only args that do not fit are tried item-wise. Every
 * array present must be the same length — a transform cannot invent the
 * correspondence between mismatched batches.
 *
 * **Per-item failures do not fail the batch.** A mapped item that fails —
 * argument validation, output validation, or a throw from the transform's own
 * `evaluate` — yields a {@link TransformItemError} marker in its slot instead
 * of throwing, so one bad source row cannot destroy the rest of its batch,
 * and the result keeps the batch's cardinality for lockstep fanout.
 * The marker propagates through nested transforms unchanged (an outer call
 * never re-wraps an inner failure) and is rejected — with the original
 * message — by the consuming step's input validation, under that step's own
 * `onItemError` policy. Only structural errors stay throw-whole: args that fit
 * neither whole nor item-wise, and batches of mismatched lengths.
 */
export function evaluateTransform(name: string, args: unknown[]): unknown {
  const transform = getTransform(name);

  const whole = transform.args.safeParse(args);
  if (whole.success) {
    return evaluateOnce(transform, whole.data);
  }

  const batchLength = resolveBatchLength(name, args);
  if (batchLength === null) {
    throw new TransformValidationError(
      name,
      "arguments",
      formatIssues(whole.error),
    );
  }

  const results: unknown[] = [];
  for (let index = 0; index < batchLength; index++) {
    const itemArgs = itemAt(args, index);
    // An item already failed by a nested transform propagates as-is, keeping
    // the inner transform's name and message. Checked before arg validation so
    // no transform (however permissive its args) can read a failure as data.
    const propagated = itemArgs.find(isTransformItemError);
    if (propagated) {
      results.push(propagated);
      continue;
    }
    const item = transform.args.safeParse(itemArgs);
    if (!item.success) {
      results.push(
        validationItemError(name, "arguments", formatIssues(item.error), index),
      );
      continue;
    }
    try {
      results.push(evaluateOnce(transform, item.data));
    } catch (error) {
      // ANY per-item throw becomes that item's marker, not just validation —
      // a transform whose evaluate() throws on one item's data is exactly the
      // "unanticipated shape" this path exists to contain, and rethrowing
      // would hand one bad row the whole chunk again. A systematic bug is not
      // masked by this: it fails every item with the same message, visible on
      // each item's StepRun.
      results.push(
        error instanceof TransformValidationError
          ? validationItemError(name, error.phase, error.detail, index)
          : transformItemError(
              name,
              index,
              `Transform "${name}" failed at batch item ${index}: ${
                error instanceof Error ? error.message : String(error)
              }`,
            ),
      );
    }
  }
  return results;
}

function evaluateOnce(
  transform: TransformDefinition,
  args: z.infer<z.ZodTuple>,
): unknown {
  const output = transform.output.safeParse(transform.evaluate(args));
  if (!output.success) {
    throw new TransformValidationError(
      transform.name,
      "output",
      formatIssues(output.error),
    );
  }
  return output.data;
}

/**
 * How many items the wired-in batch holds, or `null` when no argument is an
 * array and there is therefore nothing to map over.
 */
function resolveBatchLength(name: string, args: unknown[]): number | null {
  const lengths = args
    .filter(Array.isArray)
    .map((value: unknown[]) => value.length);

  if (lengths.length === 0) return null;

  const distinct = [...new Set(lengths)];
  if (distinct.length > 1) {
    throw new TransformValidationError(
      name,
      "arguments",
      `batched arguments hold different numbers of items (${distinct
        .sort((a, b) => a - b)
        .join(
          ", ",
        )}); arrays passed to one transform must line up item for item`,
    );
  }
  return distinct[0];
}

/** The batch narrowed to one item: arrays indexed, single values broadcast. */
function itemAt(args: unknown[], index: number): unknown[] {
  return args.map((value) => (Array.isArray(value) ? value[index] : value));
}

/** Zod issues flattened to a single readable line. */
function formatIssues(error: z.ZodError): string {
  return error.issues
    .map((issue) => {
      const at = issue.path.length > 0 ? `arg ${String(issue.path[0])}: ` : "";
      return `${at}${issue.message}`;
    })
    .join("; ");
}
